import React, { useState } from "react";
// Material Ui
import {
  SelectProps,
  FormControl,
  Select as MuiSelect,
  Box,
  PopoverOrigin,
  MenuProps,
  lighten,
  MenuItem,
  SelectChangeEvent,
  InputLabel,
} from "@mui/material";

import CancelIcon from "@mui/icons-material/Cancel";

import {
  menuItemSxProps,
  selectSxProps,
  menuSxProps,
  formControlAsChipSxProps,
  inputLabelSxPropsFn,
} from "./Select.styles";

import Chip, { SayChipProps } from "../../Chip";
import Text from "../../Text";
import { COLORS } from "../../../../constants";
import { getSizeData } from "./constants";

interface BaseProps {
  chipProps?: SayChipProps;
  asChip?: boolean;
  menuProps?: MenuProps;
  value?: string | number;
  options?: {
    label: string;
    value: string | number;
  }[];
  name: string;
  menuTitle?: string;
  size?: "small" | "medium";
  clearable?: boolean;
  valueMapping?: {
    [key: string]: string;
  };
}

interface PropsWithOptions extends BaseProps {
  options: {
    label: string;
    value: string | number;
  }[];
}
interface PropsWithChildren extends BaseProps {
  children: React.ReactNode;
}

export type CustomSelectProps = PropsWithOptions | PropsWithChildren;

const Select = ({
  asChip,
  chipProps,
  menuProps,
  onChange,
  value = "",
  children,
  name,
  options,
  size = "small",
  label,
  menuTitle,
  valueMapping,
  clearable = false,
  sx,
  ...rest
}: CustomSelectProps & SelectProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = React.useState(false);
  const anchorSet = Boolean(anchorEl);

  const sizeData = getSizeData(size);

  /**
   * force close menu
   */
  const handleClose = () => {
    if (asChip) {
      setAnchorEl(null);
    }
    setOpen(false);
  };

  /**
   * open menu
   */
  const handleOpen = () => {
    setOpen(true);
  };

  /**
   * trigger onChange event and force close menu if chip format
   * error in latest material version with onChange type
   */
  const handleChange = (
    e: SelectChangeEvent<unknown>,
    child: React.ReactNode
  ) => {
    if (asChip) {
      handleClose();
    }
    if (onChange) {
      onChange(e, child);
    }
  };

  /**
   * set anchor element for popover positioning to the chip
   */
  const handleChipClick = (event: React.MouseEvent<HTMLElement>) => {
    if (chipProps?.onClick) {
      chipProps?.onClick(event);
    }
    setAnchorEl(event.currentTarget);
  };

  /**
   * set anchor element for popover positioning to the chip (parent of delete icon)
   */
  const handleChipDeleteClick = (event: React.MouseEvent<HTMLElement>) => {
    if (chipProps?.onDelete) {
      chipProps?.onDelete(event);
    }
    if (!value || !clearable) {
      setAnchorEl(event.currentTarget.parentElement);
    }
  };

  /**
   * Determine label for chip
   */
  const getChipLabel = () => {
    if (!value) {
      return chipProps?.label;
    }
    if (valueMapping?.[value]) {
      return valueMapping[value];
    }
    return value.toString();
  };

  /**
   * chip element if used
   */
  const chip =
    asChip && chipProps ? (
      <Chip
        {...chipProps}
        deleteIcon={
          !value || !clearable ? (
            chipProps.deleteIcon
          ) : (
            <CancelIcon style={{ color: lighten(COLORS.BLACK, 0.5) }} />
          )
        }
        onClick={handleChipClick}
        onDelete={handleChipDeleteClick}
        label={getChipLabel()}
      />
    ) : null;

  /**
   * menu props, with some defaults
   */
  const MenuProps = {
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    } as PopoverOrigin,
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    } as PopoverOrigin,
    ...(asChip && { open: anchorSet, anchorEl }),
    onClose: handleClose,
    sx: menuSxProps,
    ...menuProps,
  };

  /**
   * Either parse array of options or render children
   */
  const renderOptions = () => {
    if (children) {
      return children;
    }
    /* istanbul ignore else */
    if (options) {
      return options.map((option) => (
        <MenuItem
          data-testid={`menu-item-${option.value}`}
          key={option.value}
          value={option.value}
          sx={menuItemSxProps}
        >
          <Text variant="body2">{option.label}</Text>
        </MenuItem>
      ));
    }
    // unreachable but need as typescript is requiring obove unneeded conditional
    /* istanbul ignore next */
    return null;
  };

  const consolidatedSxProps = { ...selectSxProps({ sizeData }), ...sx };

  return (
    <Box data-testid={`selectwrapper-${name}`}>
      {chip}
      <FormControl
        variant="outlined"
        fullWidth
        sx={asChip ? formControlAsChipSxProps : undefined}
      >
        {label && (
          <InputLabel sx={inputLabelSxPropsFn({ sizeData })}>
            {label}
          </InputLabel>
        )}
        <MuiSelect
          id={`${name}-select`}
          labelId={`${name}-select-label`}
          data-testid={`${name}-select`}
          value={value}
          label={label}
          MenuProps={MenuProps}
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
          onChange={handleChange}
          sx={consolidatedSxProps}
          renderValue={(value) => (
            <Text
              variant={size === "medium" ? "body2" : "caption2"}
              emphasis="medium_high"
            >
              {value as string}
            </Text>
          )}
          {...rest}
        >
          {menuTitle && (
            <Text
              component="p"
              sx={{ pl: 4, lineHeight: "20px" }}
              variant="caption"
              emphasis="disabled"
            >
              {menuTitle}
            </Text>
          )}
          {renderOptions()}
        </MuiSelect>
      </FormControl>
    </Box>
  );
};

export default Select;
